import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Camera, Globe, Cloud, History, Sparkles, ArrowRight, ShieldCheck, Phone, Leaf,
  Smartphone, FlaskConical, ShieldAlert, Droplets, AlertTriangle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { HansaLogo, PoweredByBadge } from "../components/HansaLogo";

export default function LandingPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1C1C1C]">
      {/* Nav */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <HansaLogo size={48} />
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher compact />
          <Link
            to="/download"
            data-testid="landing-download-link"
            className="flex items-center gap-1.5 bg-[#D4A373]/20 hover:bg-[#D4A373]/30 text-[#1B4332] border border-[#D4A373]/40 font-semibold px-3 py-2 rounded-full text-xs sm:text-sm transition-colors"
          >
            <Smartphone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden xs:inline sm:inline">{t("nav.download")}</span>
          </Link>
          {user ? (
            <Link
              to="/dashboard"
              data-testid="landing-to-dashboard"
              className="bg-[#1B4332] text-white hover:bg-[#2D6A4F] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
            >
              {t("nav.home")}
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="landing-login-link" className="text-[#1B4332] hover:text-[#2D6A4F] font-semibold text-sm">
                {t("common.login")}
              </Link>
              <Link
                to="/signup"
                data-testid="landing-signup-link"
                className="bg-[#1B4332] text-white hover:bg-[#2D6A4F] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
              >
                {t("common.signup")}
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="cd-grain relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div className="cd-fade-up">
            <span data-testid="landing-tagline" className="inline-flex items-center gap-2 bg-[#F4F1EA] text-[#1B4332] border border-[#E5E0D8] px-3 py-1.5 rounded-full text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-[#D4A373]" />
              {t("landing.tagline")}
            </span>
            <h1 className="cd-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5 leading-[1.05]">
              {t("landing.h1.line1")}
              <br />
              <span className="text-[#1B4332]">{t("landing.h1.line2")}</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#2D3A35] leading-relaxed max-w-lg">
              {t("landing.subtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-cta-primary"
                onClick={() => nav(user ? "/dashboard" : "/login")}
                className="bg-[#1B4332] text-white hover:bg-[#2D6A4F] px-7 py-4 rounded-full font-bold flex items-center justify-center gap-2 cd-lift"
              >
                <Phone className="w-5 h-5" />
                {t("auth.useMobile")}
                <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/login/email"
                data-testid="hero-cta-secondary"
                className="bg-white text-[#1B4332] border-2 border-[#1B4332] hover:bg-[#F4F1EA] px-7 py-4 rounded-full font-bold flex items-center justify-center gap-2 cd-lift"
              >
                <Camera className="w-4 h-4" /> {t("auth.useEmail")}
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-5 text-sm text-[#52796F]">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> {t("landing.trusted")}
              </div>
              <div className="flex items-center gap-1.5">
                <Globe className="w-4 h-4" /> {t("landing.languages")}
              </div>
            </div>
          </div>

          <div className="relative cd-fade-up">
            <div className="absolute -top-6 -left-6 w-40 h-40 bg-[#D4A373]/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -right-8 w-52 h-52 bg-[#52796F]/20 rounded-full blur-3xl" />
            <div className="relative rounded-3xl overflow-hidden border border-[#E5E0D8] shadow-xl bg-gradient-to-br from-[#F4F1EA] to-[#FDFBF7]">
              <img
                src="/founder-sanket.jpeg"
                alt="Sanket — Farmer & Founder, Ramkishan Agri Innovate"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 border border-[#E5E0D8]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#2B9348]/15 flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-[#2B9348]" />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-[#1C1C1C]">Sanket · संस्थापक किसान</div>
                    <div className="text-xs text-[#52796F]">Ramkishan Agri Innovate · हंसा को बनाते हुए</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Medicine Check Showcase */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <div className="bg-gradient-to-br from-[#1B4332] to-[#2D6A4F] rounded-3xl overflow-hidden border border-[#1B4332] relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4A373]/20 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none" />
          <div className="grid md:grid-cols-2 gap-0 relative">
            <div className="p-8 sm:p-12 text-white">
              <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-3 py-1.5 rounded-full text-xs font-bold text-[#D4A373]">
                <FlaskConical className="w-3.5 h-3.5" /> {t("landing.medCheck.tagline")}
              </span>
              <h2 className="cd-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 leading-tight">
                {t("landing.medCheck.h2.line1")}
                <br />
                <span className="text-[#D4A373]">{t("landing.medCheck.h2.line2")}</span>
              </h2>
              <p className="mt-5 text-base text-white/85 leading-relaxed max-w-md">
                {t("landing.medCheck.body")}
              </p>
              <ul className="mt-6 space-y-2.5">
                <MedBullet icon={<ShieldCheck className="w-4 h-4 text-[#D4A373]" />} text={t("landing.medCheck.b1")} />
                <MedBullet icon={<Droplets className="w-4 h-4 text-[#D4A373]" />} text={t("landing.medCheck.b2")} />
                <MedBullet icon={<AlertTriangle className="w-4 h-4 text-[#D4A373]" />} text={t("landing.medCheck.b3")} />
                <MedBullet icon={<Leaf className="w-4 h-4 text-[#D4A373]" />} text={t("landing.medCheck.b4")} />
              </ul>
              <button
                data-testid="landing-med-check-cta"
                onClick={() => nav(user ? "/chemical-check" : "/login")}
                className="mt-7 bg-[#D4A373] hover:bg-[#c89460] text-[#1C1C1C] px-6 py-3.5 rounded-full font-bold flex items-center gap-2 cd-lift"
              >
                <FlaskConical className="w-5 h-5" />
                {t("landing.medCheck.cta")}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Right visual: stylized verdict card */}
            <div className="relative p-8 sm:p-12 flex items-center justify-center min-h-[360px]">
              <div className="relative bg-white rounded-2xl border border-[#E5E0D8] shadow-2xl p-5 max-w-sm w-full cd-fade-up">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#1B4332] flex items-center justify-center">
                    <FlaskConical className="w-4 h-4 text-[#D4A373]" />
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-[#52796F] font-bold">
                    {t("landing.medCheck.cardLabel")}
                  </div>
                </div>
                <div className="bg-[#2B9348]/10 border border-[#2B9348]/30 rounded-xl p-3 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#2B9348] shrink-0" />
                  <div className="font-bold text-[#2B9348] text-sm">{t("landing.medCheck.cardSafe")}</div>
                </div>
                <div className="mt-3 text-sm">
                  <div className="font-bold text-[#1C1C1C]">Mancozeb 75% WP</div>
                  <div className="text-xs text-[#52796F]">Mancozeb · Fungicide</div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="bg-[#F4F1EA] rounded-lg p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-[#52796F] font-semibold flex items-center gap-1">
                      <Droplets className="w-3 h-3" /> {t("chem.dosage")}
                    </div>
                    <div className="text-xs font-bold mt-1 text-[#1C1C1C]">2 g / litre</div>
                  </div>
                  <div className="bg-[#F4F1EA] rounded-lg p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-[#52796F] font-semibold flex items-center gap-1">
                      <Leaf className="w-3 h-3" /> {t("chem.phi")}
                    </div>
                    <div className="text-xs font-bold mt-1 text-[#1C1C1C]">7 days</div>
                  </div>
                </div>
                <div className="mt-3 bg-[#D90429]/5 border border-[#D90429]/20 rounded-lg p-2.5 flex items-start gap-2">
                  <ShieldAlert className="w-3.5 h-3.5 text-[#D90429] shrink-0 mt-0.5" />
                  <div className="text-[11px] text-[#2D3A35] leading-snug">
                    {t("landing.medCheck.cardWarn")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="cd-heading text-3xl sm:text-4xl font-extrabold">{t("landing.howH2")}</h2>
          <p className="text-[#2D3A35] mt-3">{t("landing.howSub")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <Feature icon={<Camera className="w-6 h-6 text-[#1B4332]" />} title={t("landing.feat.diag.title")} body={t("landing.feat.diag.body")} testid="feature-diagnosis" />
          <Feature icon={<FlaskConical className="w-6 h-6 text-[#D4A373]" />} title={t("landing.feat.medCheck.title")} body={t("landing.feat.medCheck.body")} testid="feature-med-check" />
          <Feature icon={<Sparkles className="w-6 h-6 text-[#E07A5F]" />} title={t("landing.feat.treat.title")} body={t("landing.feat.treat.body")} testid="feature-treatment" />
          <Feature icon={<Cloud className="w-6 h-6 text-[#52796F]" />} title={t("landing.feat.weather.title")} body={t("landing.feat.weather.body")} testid="feature-weather" />
          <Feature icon={<Globe className="w-6 h-6 text-[#D4A373]" />} title={t("landing.feat.lang.title")} body={t("landing.feat.lang.body")} testid="feature-languages" />
          <Feature icon={<History className="w-6 h-6 text-[#1B4332]" />} title={t("landing.feat.history.title")} body={t("landing.feat.history.body")} testid="feature-history" />
          <Feature icon={<ShieldCheck className="w-6 h-6 text-[#2B9348]" />} title={t("landing.feat.models.title")} body={t("landing.feat.models.body")} testid="feature-models" />
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] py-6 text-center text-sm text-[#52796F]">
        <div className="flex flex-col items-center gap-1.5">
          <PoweredByBadge />
          <div className="text-xs">हमारी संस्कृति ही कृषि है · © {new Date().getFullYear()} Hansa</div>
          <Link to="/privacy" data-testid="landing-privacy-link" className="text-xs text-[#52796F] hover:text-[#1B4332] underline">
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  );
}

const Feature = ({ icon, title, body, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl p-6 border border-[#E5E0D8] shadow-sm cd-lift">
    <div className="w-12 h-12 rounded-2xl bg-[#F4F1EA] flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="cd-heading font-bold text-lg text-[#1C1C1C]">{title}</h3>
    <p className="text-[#2D3A35] text-sm mt-2 leading-relaxed">{body}</p>
  </div>
);

const MedBullet = ({ icon, text }) => (
  <li className="flex items-start gap-2.5 text-sm text-white/90">
    <span className="shrink-0 mt-0.5">{icon}</span>
    <span className="leading-relaxed">{text}</span>
  </li>
);
